import { useEffect, useState, useCallback, Component } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { flightOpsApi } from "@/api";
import {
  Plane, RefreshCw, Search, X, AlertTriangle, Clock,
  Map as MapIcon, List,
  Zap, ShieldCheck, ShieldOff, ShieldAlert,
  Activity, Users, Navigation, ExternalLink, Loader
} from "lucide-react";

// ─── Leaflet icon fix (run once, safely) ─────────────────────────────────────
if (L && L.Icon && L.Icon.Default && L.Icon.Default.prototype) {
  delete L.Icon.Default.prototype._getIconUrl;
}

const STATUS_COLOR = {
  active:    "#22c55e",
  "en-route":"#22c55e",
  scheduled: "#3b82f6",
  landed:    "#64748b",
  cancelled: "#ef4444",
  unknown:   "#475569",
};
const FDP_COLOR = { ok: "#22c55e", warning: "#f59e0b", critical: "#ef4444" };

function makePlaneIcon(direction = 0, status = "active") {
  const color = STATUS_COLOR[status] || "#64748b";
  return L.divIcon({
    html: `<div style="transform:rotate(${direction}deg);color:${color};font-size:18px;filter:drop-shadow(0 0 3px rgba(0,0,0,0.9));line-height:1">✈</div>`,
    className: "",
    iconSize:  [20, 20],
    iconAnchor:[10, 10],
  });
}

function makeAirportIcon(isHub, count = 0) {
  const size = isHub ? 10 : 7;
  const color = isHub ? "#3b82f6" : "#475569";
  const border = isHub ? "#60a5fa" : "#64748b";
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;border:1.5px solid ${border};position:relative">
             ${count > 0 ? `<div style="position:absolute;top:-8px;left:50%;transform:translateX(-50%);background:#1e293b;color:#94a3b8;font-size:8px;font-family:monospace;padding:0 2px;border-radius:2px;white-space:nowrap">${count}</div>` : ""}
           </div>`,
    className: "",
    iconSize:  [size, size],
    iconAnchor:[size / 2, size / 2],
  });
}

// Curved arc between two lat/lng points
function arcPath(lat1, lng1, lat2, lng2, steps = 30) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const lat = lat1 + (lat2 - lat1) * t;
    const lng = lng1 + (lng2 - lng1) * t;
    const curve = Math.sin(Math.PI * t) * 1.5;
    const dLat = lat2 - lat1, dLng = lng2 - lng1;
    const len = Math.sqrt(dLat * dLat + dLng * dLng) || 1;
    pts.push([lat - (dLng / len) * curve, lng + (dLat / len) * curve]);
  }
  return pts;
}

// ─── Map Error Boundary ───────────────────────────────────────────────────────
class MapErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-xs p-8 text-center">
        <div>
          <AlertTriangle size={24} className="mx-auto mb-3 text-amber-400" />
          <div>Map unavailable in this environment.</div>
          <div className="text-xs mt-1 opacity-50">{this.state.error?.message}</div>
        </div>
      </div>
    );
    return this.props.children;
  }
}
function TriggerDisruptionModal({ flight, assignment, onClose, onSuccess }) {
  const [dtype, setDtype]     = useState("crew_sick_call");
  const [severity, setSev]    = useState("high");
  const [loading, setLoading] = useState(false);

  const types = [
    { value: "crew_sick_call",    label: "Crew Sick Call" },
    { value: "crew_no_show",      label: "Crew No-Show" },
    { value: "fdp_limit_breach",  label: "FDP Limit Breach" },
    { value: "departure_delay",   label: "Departure Delay" },
  ];

  async function handleTrigger() {
    setLoading(true);
    try {
      const res = await flightOpsApi.triggerDisruption(flight.flight_iata, { disruption_type: dtype, severity });
      onSuccess(res.data.disruption_id);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create disruption");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" data-testid="trigger-disruption-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-400" />
            <span className="text-sm font-bold text-foreground">Trigger Disruption</span>
            <span className="font-mono text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">{flight.flight_iata}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-xs text-muted-foreground bg-secondary/30 rounded-sm px-3 py-2">
            Route: <span className="text-foreground font-mono">{flight.departure?.iata} → {flight.arrival?.iata}</span>
            {" "}&nbsp; Crew: <span className="text-foreground">{assignment?.captain?.name || "—"}</span>
            {assignment?.fo && <> / {assignment.fo.name}</>}
          </div>

          <div>
            <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-2">Disruption Type</label>
            <div className="grid grid-cols-2 gap-2">
              {types.map(t => (
                <button
                  key={t.value}
                  onClick={() => setDtype(t.value)}
                  className={`px-3 py-2 rounded-sm text-xs text-left transition-all border ${
                    dtype === t.value ? "bg-primary/15 border-primary text-primary font-bold" : "bg-secondary/30 border-border text-muted-foreground hover:border-primary/40"
                  }`}
                  data-testid={`dtype-${t.value}`}
                >{t.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-2">Severity</label>
            <div className="flex gap-2">
              {["low", "medium", "high", "critical"].map(s => (
                <button key={s} onClick={() => setSev(s)}
                  className={`flex-1 px-2 py-1.5 rounded-sm text-xs capitalize transition-all border ${
                    severity === s
                      ? s === "critical" ? "bg-red-500/20 border-red-500 text-red-400 font-bold"
                        : s === "high"   ? "bg-amber-500/20 border-amber-500 text-amber-400 font-bold"
                        : "bg-primary/15 border-primary text-primary font-bold"
                      : "bg-secondary/30 border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 px-4 pb-4">
          <button onClick={onClose} className="flex-1 px-3 py-2 text-xs rounded-sm bg-secondary text-muted-foreground hover:text-foreground border border-border">
            Cancel
          </button>
          <button
            onClick={handleTrigger}
            disabled={loading}
            className="flex-1 px-3 py-2 text-xs rounded-sm bg-red-600 hover:bg-red-500 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="confirm-trigger-disruption"
          >
            {loading ? <Loader size={12} className="animate-spin" /> : <Zap size={12} />}
            {loading ? "Creating..." : "Trigger Disruption"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Disruption status badge ──────────────────────────────────────────────────
function DisruptionBadge({ status }) {
  if (!status) return null;
  if (status === "resolved") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-500/15 border border-green-500/30 rounded text-green-400 font-bold text-xs">
      <ShieldCheck size={9} /> RESOLVED
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/30 rounded text-amber-400 font-bold text-xs animate-pulse">
      <AlertTriangle size={9} /> DISRUPTION
    </span>
  );
}
function FDPBar({ fdp }) {
  if (!fdp) return null;
  const pct   = Math.min(100, (fdp.fdp_hours / fdp.max_fdp) * 100);
  const color = FDP_COLOR[fdp.fdp_status] || "#64748b";
  return (
    <div className="space-y-0.5" title={`FDP: ${fdp.fdp_hours}h elapsed / ${fdp.fdp_remaining}h remaining`}>
      <div className="flex justify-between text-xs" style={{ color }}>
        <span className="font-mono font-bold">{fdp.fdp_hours}h</span>
        <span className="text-muted-foreground text-xs">{fdp.fdp_remaining}h left</span>
      </div>
      <div className="h-1 bg-secondary rounded-full overflow-hidden w-24">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── Flight Map ───────────────────────────────────────────────────────────────
function FlightMap({ flights, assignments, airports, onFlightSelect }) {
  const activeAirports = new Set();
  const flightCounts   = {};
  flights.forEach(f => {
    const dep = f.departure?.iata;
    const arr = f.arrival?.iata;
    if (dep) { activeAirports.add(dep); flightCounts[dep] = (flightCounts[dep] || 0) + 1; }
    if (arr) { activeAirports.add(arr); }
  });

  return (
    <MapContainer
      center={[20.5, 78.9]}
      zoom={5}
      style={{ height: "100%", width: "100%", background: "#0f172a" }}
      className="rounded-sm"
      data-testid="flight-map"
    >
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        attribution='&copy; OpenStreetMap contributors &copy; CARTO'
        subdomains="abcd"
        maxZoom={18}
      />

      {/* Route arcs */}
      {flights.map(f => {
        const depC = airports[f.departure?.iata];
        const arrC = airports[f.arrival?.iata];
        if (!depC || !arrC) return null;
        const color  = STATUS_COLOR[f.status] || "#475569";
        const path   = arcPath(depC.lat, depC.lng, arrC.lat, arrC.lng);
        const isActive = f.status === "active" || f.status === "en-route";
        return (
          <Polyline
            key={`arc-${f.flight_iata}`}
            positions={path}
            pathOptions={{ color, weight: isActive ? 1.5 : 0.8, opacity: isActive ? 0.6 : 0.25, dashArray: isActive ? null : "4 4" }}
            eventHandlers={{ click: () => onFlightSelect(f) }}
          />
        );
      })}

      {/* Airport markers */}
      {Object.entries(airports).map(([iata, ap]) => {
        if (!activeAirports.has(iata)) return null;
        return (
          <Marker
            key={`apt-${iata}`}
            position={[ap.lat, ap.lng]}
            icon={makeAirportIcon(ap.hub, flightCounts[iata] || 0)}
          >
            <Popup className="leaflet-popup-dark">
              <div style={{ background: "#1e293b", color: "#f1f5f9", padding: "8px 10px", borderRadius: 4, fontSize: 12, minWidth: 140 }}>
                <div style={{ fontWeight: "bold", marginBottom: 2 }}>{iata} — {ap.city}</div>
                <div style={{ color: "#94a3b8", fontSize: 11 }}>{ap.name}</div>
                {flightCounts[iata] && <div style={{ color: "#3b82f6", marginTop: 4 }}>{flightCounts[iata]} departures</div>}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Active flight plane icons */}
      {flights.map(f => {
        const asgn = assignments[f.flight_iata];
        const pos  = asgn?.position;
        if (!pos) return null;
        return (
          <Marker
            key={`plane-${f.flight_iata}`}
            position={[pos.lat, pos.lng]}
            icon={makePlaneIcon(pos.direction || 0, f.status)}
            zIndexOffset={1000}
            eventHandlers={{ click: () => onFlightSelect(f) }}
          >
            <Popup className="leaflet-popup-dark">
              <div style={{ background: "#1e293b", color: "#f1f5f9", padding: "8px 10px", borderRadius: 4, fontSize: 12, minWidth: 180 }}>
                <div style={{ fontWeight: "bold", fontSize: 13, marginBottom: 4 }}>{f.flight_iata}</div>
                <div style={{ color: "#94a3b8", marginBottom: 4 }}>{f.departure?.iata} → {f.arrival?.iata}</div>
                {asgn?.captain && <div style={{ color: "#22c55e", fontSize: 11 }}>CPT: {asgn.captain.name}</div>}
                {asgn?.fo      && <div style={{ color: "#60a5fa", fontSize: 11 }}>FO: {asgn.fo.name}</div>}
                {asgn?.fdp     && <div style={{ color: FDP_COLOR[asgn.fdp.fdp_status] || "#94a3b8", marginTop: 4, fontSize: 11 }}>
                  FDP: {asgn.fdp.fdp_hours}h / {asgn.fdp.max_fdp}h ({asgn.fdp.fdp_remaining}h remaining)
                </div>}
                {pos.estimated && <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>Position estimated ({Math.round((pos.progress || 0) * 100)}% complete)</div>}
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
}

// ─── Flight Detail Modal ──────────────────────────────────────────────────────
function FlightDetailModal({ flight, assignment, onClose, onTrigger }) {
  if (!flight) return null;
  const dep = flight.departure || {};
  const arr = flight.arrival   || {};
  const fdp = assignment?.fdp;
  const statusColor = STATUS_COLOR[flight.status] || "#64748b";

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[500] flex items-center justify-center p-4" onClick={onClose} data-testid="flight-detail-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Plane size={18} className="text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-black font-mono text-foreground">{flight.flight_iata}</span>
                <span className="text-xs px-2 py-0.5 rounded-sm font-bold capitalize" style={{ background: statusColor + "20", color: statusColor }}>
                  {flight.status}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{dep.airport} → {arr.airport}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Route */}
          <div className="grid grid-cols-2 gap-3">
            {[{label:"Departure", d: dep}, {label:"Arrival", d: arr}].map(({label, d}) => (
              <div key={label} className="bg-secondary/30 rounded-sm p-3 border border-border/50">
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-bold">{label}</div>
                <div className="text-2xl font-black font-mono text-foreground mb-1">{d.iata || "—"}</div>
                <div className="text-xs text-muted-foreground mb-2">{d.airport}</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">Scheduled</span><span className="font-mono text-foreground">{d.scheduled ? new Date(d.scheduled).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "—"}</span></div>
                  {d.estimated && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Estimated</span><span className="font-mono text-foreground">{new Date(d.estimated).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</span></div>}
                  {d.gate && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Gate</span><span className="font-mono">{d.gate}</span></div>}
                  {d.delay_min > 0 && <div className="flex justify-between text-xs"><span className="text-amber-400">Delay</span><span className="font-mono text-amber-400">+{d.delay_min} min</span></div>}
                </div>
              </div>
            ))}
          </div>

          {/* Crew Assignment */}
          {assignment && (
            <div className="border border-border/50 rounded-sm p-3 bg-card/30">
              <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <Users size={11} /> Simulated Crew Assignment
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Captain",        crew: assignment.captain, color: "#22c55e" },
                  { label: "First Officer",  crew: assignment.fo,      color: "#3b82f6" },
                ].map(({ label, crew, color }) => (
                  <div key={label} className="bg-secondary/20 rounded-sm p-3 border border-border/50">
                    <div className="text-xs text-muted-foreground mb-1">{label}</div>
                    {crew ? (
                      <>
                        <div className="font-bold text-sm" style={{ color }}>{crew.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{crew.employee_id} · {crew.base}</div>
                        {crew.valid_ratings?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {crew.valid_ratings.map(r => (
                              <span key={r} className="text-xs px-1 py-0.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded font-mono">{r}</span>
                            ))}
                          </div>
                        )}
                      </>
                    ) : <div className="text-xs text-muted-foreground italic">No crew available</div>}
                  </div>
                ))}
              </div>

              {/* FDP Panel */}
              {fdp && (
                <div className="mt-3 bg-secondary/20 rounded-sm p-3 border border-border/50">
                  <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Clock size={11} /> Flight Duty Period
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-2xl font-black font-mono" style={{ color: FDP_COLOR[fdp.fdp_status] }}>{fdp.fdp_hours}h</span>
                      <span className="text-xs text-muted-foreground ml-2">of {fdp.max_fdp}h max</span>
                    </div>
                    <div className={`text-xs font-bold px-2 py-1 rounded-sm ${
                      fdp.fdp_status === "critical" ? "bg-red-500/15 text-red-400" :
                      fdp.fdp_status === "warning"  ? "bg-amber-500/15 text-amber-400" :
                                                      "bg-green-500/15 text-green-400"
                    }`}>{fdp.fdp_remaining}h remaining</div>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{
                      width: `${Math.min(100,(fdp.fdp_hours/fdp.max_fdp)*100)}%`,
                      background: FDP_COLOR[fdp.fdp_status]
                    }} />
                  </div>
                  {fdp.fdp_status !== "ok" && (
                    <div className={`mt-2 text-xs flex items-center gap-1 ${fdp.fdp_status === "critical" ? "text-red-400" : "text-amber-400"}`}>
                      <AlertTriangle size={10} />
                      {fdp.fdp_status === "critical" ? "CRITICAL: FDP limit approaching — recovery crew may be required" : "WARNING: Less than 3h FDP remaining"}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        {/* Disruption History (when triggered from this flight) */}
          {assignment?.disruption_status && (
            <div className={`border rounded-sm p-3 ${
              assignment.disruption_status === "resolved"
                ? "border-green-500/30 bg-green-500/5"
                : "border-amber-500/30 bg-amber-500/5"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                  {assignment.disruption_status === "resolved"
                    ? <><ShieldCheck size={11} className="text-green-400" /><span className="text-green-400">Disruption Resolved</span></>
                    : <><AlertTriangle size={11} className="text-amber-400" /><span className="text-amber-400">Active Disruption</span></>
                  }
                </div>
                <DisruptionBadge status={assignment.disruption_status} />
              </div>
              <div className="text-xs text-muted-foreground mb-2">{assignment.disruption_title}</div>

              {assignment.disruption_status === "resolved" && assignment.replacement_crew?.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground font-bold mb-1">Replacement Crew Activated:</div>
                  {assignment.replacement_crew.map((rc, i) => (
                    <div key={i} className="flex items-center gap-2 bg-green-500/10 rounded-sm px-2 py-1.5">
                      <ShieldCheck size={10} className="text-green-400 shrink-0" />
                      <div>
                        <span className="text-xs font-bold text-green-400">{rc.name}</span>
                        {rc.rank && <span className="text-xs text-muted-foreground ml-1">· {rc.rank}</span>}
                        {rc.base && <span className="text-xs text-muted-foreground ml-1">· {rc.base}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {(flight.aircraft?.type || flight.aircraft?.registration) && (
            <div className="bg-secondary/20 rounded-sm p-3 border border-border/50">
              <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-2">Aircraft</div>
              <div className="flex items-center gap-4 text-sm">
                <div><span className="text-muted-foreground text-xs">Type: </span><span className="font-mono font-bold">{flight.aircraft.type || "—"}</span></div>
                <div><span className="text-muted-foreground text-xs">Reg: </span><span className="font-mono font-bold">{flight.aircraft.registration || "—"}</span></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4">
          {assignment?.disruption_status === "resolved" ? (
            <div className="flex items-center justify-center gap-2 py-2 bg-green-500/10 border border-green-500/25 rounded-sm text-green-400 text-sm font-bold">
              <ShieldCheck size={14} /> Resolution Applied — New Crew Assigned
            </div>
          ) : assignment?.disruption_status && assignment.disruption_status !== "resolved" ? (
            <button
              onClick={() => { onClose(); }}
              className="w-full py-2.5 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-600/40 text-amber-400 text-sm font-bold rounded-sm transition-all flex items-center justify-center gap-2"
              data-testid="view-disruption-btn"
            >
              <AlertTriangle size={14} /> Disruption Active — View in Console
            </button>
          ) : (
            <button
              onClick={onTrigger}
              className="w-full py-2.5 bg-red-600/20 hover:bg-red-600/30 border border-red-600/40 hover:border-red-500 text-red-400 hover:text-red-300 text-sm font-bold rounded-sm transition-all flex items-center justify-center gap-2"
              data-testid="open-trigger-disruption-btn"
            >
              <Zap size={14} /> Trigger Disruption for This Flight
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Flight Row ───────────────────────────────────────────────────────────────
function FlightRow({ flight, assignment, onClick }) {
  const dep = flight.departure || {};
  const arr = flight.arrival   || {};
  const fdp = assignment?.fdp;
  const dStatus = assignment?.disruption_status;
  const statusColor = STATUS_COLOR[flight.status] || "#64748b";

  // Determine displayed crew (replacement if resolved)
  const captain = assignment?.captain;
  const fo      = assignment?.fo;

  return (
    <tr
      onClick={onClick}
      className={`border-b border-border/30 hover:bg-primary/5 cursor-pointer transition-colors group ${
        dStatus === "resolved" ? "bg-green-500/3" :
        dStatus && dStatus !== "resolved" ? "bg-amber-500/3" : ""
      }`}
      data-testid={`flight-row-${flight.flight_iata}`}
    >
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusColor }} />
          <span className="font-mono font-bold text-sm text-foreground group-hover:text-primary transition-colors">{flight.flight_iata}</span>
          {dStatus && <DisruptionBadge status={dStatus} />}
        </div>
      </td>
      <td className="px-4 py-2.5">
        <span className="text-xs px-1.5 py-0.5 rounded-sm font-bold capitalize" style={{ background: statusColor + "20", color: statusColor }}>
          {flight.status}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <span className="font-mono text-sm font-bold text-foreground">{dep.iata}</span>
        <span className="text-muted-foreground mx-1">→</span>
        <span className="font-mono text-sm font-bold text-foreground">{arr.iata}</span>
      </td>
      <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">
        {dep.scheduled ? new Date(dep.scheduled).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "—"}
        {dep.delay_min > 0 && <span className="text-amber-400 ml-1">+{dep.delay_min}m</span>}
      </td>
      <td className="px-4 py-2.5">
        {assignment ? (
          <div className="space-y-0.5">
            {captain && (
              <div className="flex items-center gap-1">
                <span className={`text-xs truncate max-w-[130px] ${captain.is_replacement ? "text-green-400 font-bold" : "text-green-400"}`} title={captain.name}>
                  {captain.name}
                </span>
                {captain.is_replacement && <span className="text-xs text-green-500 font-mono">(new)</span>}
              </div>
            )}
            {fo && (
              <div className="flex items-center gap-1">
                <span className={`text-xs truncate max-w-[130px] ${fo.is_replacement ? "text-blue-300 font-bold" : "text-blue-400"}`} title={fo.name}>
                  {fo.name}
                </span>
                {fo.is_replacement && <span className="text-xs text-blue-400 font-mono">(new)</span>}
              </div>
            )}
          </div>
        ) : <span className="text-xs text-muted-foreground/50 italic">No crew at base</span>}
      </td>
      <td className="px-4 py-2.5">
        {fdp ? <FDPBar fdp={fdp} /> : <span className="text-xs text-muted-foreground">—</span>}
      </td>
      <td className="px-4 py-2.5">
        <span className="text-xs text-muted-foreground font-mono">{flight.aircraft?.type || "—"}</span>
      </td>
    </tr>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function FlightOpsBoard() {
  const navigate = useNavigate();

  const [flights,     setFlights]     = useState([]);
  const [assignments, setAssignments] = useState({});
  const [airports,    setAirports]    = useState({});
  const [loading,     setLoading]     = useState(true);
  const [simLoading,  setSimLoading]  = useState(false);
  const [error,       setError]       = useState(null);
  const [updatedAt,   setUpdatedAt]   = useState(null);
  const [fromCache,   setFromCache]   = useState(false);

  const [view,         setView]         = useState("table");  // "table" | "map"
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected,     setSelected]     = useState(null);
  const [triggerFlight, setTriggerFlight] = useState(null);

  const fetchFlights = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await flightOpsApi.getLive(force);
      setFlights(res.data.flights || []);
      setUpdatedAt(res.data.updated_at);
      setFromCache(res.data.from_cache);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to fetch flight data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignments = useCallback(async (force = false) => {
    setSimLoading(true);
    try {
      const res = await flightOpsApi.getSimulatedAssignments(force);
      setAssignments(res.data.assignments || {});
    } catch (e) {
      console.warn("Sim assignments error:", e?.response?.data?.detail);
    } finally {
      setSimLoading(false);
    }
  }, []);

  const fetchAirports = useCallback(async () => {
    try {
      const res = await flightOpsApi.getAirports();
      setAirports(res.data.airports || {});
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchAirports();
    fetchFlights().then(() => fetchAssignments());
    // Poll assignments every 30s to pick up disruption resolutions
    const timer = setInterval(() => fetchAssignments(), 30000);
    return () => clearInterval(timer);
  }, [fetchFlights, fetchAssignments, fetchAirports]);

  const handleRefresh = async () => {
    await fetchFlights(true);
    await fetchAssignments(true);
  };

  const handleDisruptionSuccess = (disruptionId) => {
    setTriggerFlight(null);
    setSelected(null);
    navigate(`/roster/disruption?highlight=${disruptionId}`);
  };

  // Filter + deduplicate by flight_iata (AviationStack can return dupes)
  const deduped  = Array.from(new Map(flights.map(f => [f.flight_iata, f])).values());
  const filtered = deduped.filter(f => {
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      f.flight_iata?.toLowerCase().includes(q) ||
      f.departure?.iata?.toLowerCase().includes(q) ||
      f.arrival?.iata?.toLowerCase().includes(q) ||
      f.departure?.airport?.toLowerCase().includes(q) ||
      f.arrival?.airport?.toLowerCase().includes(q) ||
      f.aircraft?.registration?.toLowerCase().includes(q)
    );
  });

  const counts = {
    active:      flights.filter(f => f.status === "active" || f.status === "en-route").length,
    scheduled:   flights.filter(f => f.status === "scheduled").length,
    landed:      flights.filter(f => f.status === "landed").length,
    cancelled:   flights.filter(f => f.status === "cancelled").length,
    fdpWarning:  Object.values(assignments).filter(a => a.fdp?.fdp_status !== "ok").length,
    disruptions: Object.values(assignments).filter(a => a.disruption_status && a.disruption_status !== "resolved").length,
    resolved:    Object.values(assignments).filter(a => a.disruption_status === "resolved").length,
  };

  return (
    <div className="flex flex-col h-full bg-background" data-testid="flight-ops-board">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary/10 rounded-sm flex items-center justify-center">
            <Plane size={16} className="text-primary" />
          </div>
          <div>
            <h1 className="text-base font-black tracking-tight text-foreground">Flight Operations Live</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse" />
              QP · DGCA approved · {flights.length} flights tracked
              {fromCache && <span className="text-amber-500">(cached)</span>}
              {updatedAt && <span>· {new Date(updatedAt).toLocaleTimeString()}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-secondary rounded-sm border border-border overflow-hidden">
            <button onClick={() => setView("table")} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-all ${view==="table" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-table-btn">
              <List size={12} /> Table
            </button>
            <button onClick={() => setView("map")} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 transition-all ${view==="map" ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:text-foreground"}`} data-testid="view-map-btn">
              <MapIcon size={12} /> Map
            </button>
          </div>
          <button onClick={handleRefresh} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground rounded-sm border border-border transition-all disabled:opacity-50" data-testid="refresh-live-btn">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh Live
          </button>
        </div>
      </div>

      {/* Quota warning */}
      <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-1.5 text-xs text-amber-400 flex items-center gap-2 shrink-0">
        <AlertTriangle size={10} /> Free tier: 100 req/month. Data cached 10 min. Crew assignments are simulated for demonstration.
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-5 divide-x divide-border/50 border-b border-border shrink-0">
        {[
          { label: "Active / En-Route",  value: counts.active,      color: "#22c55e", icon: Activity },
          { label: "Scheduled",          value: counts.scheduled,   color: "#3b82f6", icon: Clock },
          { label: "Landed",             value: counts.landed,      color: "#64748b", icon: Navigation },
          { label: "Active Disruptions", value: counts.disruptions, color: "#f59e0b", icon: AlertTriangle },
          { label: "Resolved",           value: counts.resolved,    color: "#22c55e", icon: ShieldCheck },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="px-5 py-3 flex items-center gap-3">
            <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: color + "20" }}>
              <Icon size={13} style={{ color }} />
            </div>
            <div>
              <div className="text-xl font-black font-mono leading-none" style={{ color }}>{value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <AlertTriangle size={32} className="text-red-400 mx-auto" />
              <div className="text-sm text-red-400 font-bold">{error}</div>
              <button onClick={() => fetchFlights(true)} className="px-4 py-2 text-xs bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/20">Retry</button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <RefreshCw size={24} className="text-primary mx-auto animate-spin" />
              <div className="text-sm text-muted-foreground">Fetching live flights...</div>
            </div>
          </div>
        ) : view === "map" ? (
          /* ── MAP VIEW ─────────────────────────────────────────────────── */
          <MapErrorBoundary>
          <div className="flex-1 relative" data-testid="map-view">
            {Object.keys(airports).length > 0 ? (
              <FlightMap
                flights={filtered}
                assignments={assignments}
                airports={airports}
                onFlightSelect={setSelected}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <RefreshCw size={20} className="animate-spin mr-2" /> Loading map data...
              </div>
            )}
            {/* Map legend */}
            <div className="absolute bottom-4 left-4 bg-card/90 backdrop-blur border border-border rounded-sm p-3 z-[400] space-y-1.5 text-xs">
              {Object.entries({ "Active/En-Route": "#22c55e", "Scheduled": "#3b82f6", "Landed": "#64748b", "Cancelled": "#ef4444" }).map(([lbl, clr]) => (
                <div key={lbl} className="flex items-center gap-2">
                  <div className="w-3 h-px" style={{ background: clr }} />
                  <span className="text-muted-foreground">{lbl}</span>
                </div>
              ))}
            </div>
            {simLoading && (
              <div className="absolute top-4 right-4 bg-card/90 border border-border rounded-sm px-3 py-2 z-[400] text-xs flex items-center gap-2 text-muted-foreground">
                <Loader size={10} className="animate-spin" /> Simulating crew...
              </div>
            )}
          </div>
          </MapErrorBoundary>
        ) : (
          /* ── TABLE VIEW ───────────────────────────────────────────────── */
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Filters */}
            <div className="flex items-center gap-3 px-4 py-2 border-b border-border/50 shrink-0">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search flight, airport..."
                  className="pl-8 pr-3 py-1.5 text-xs bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary w-56"
                  data-testid="flight-search-input"
                />
              </div>
              <div className="flex gap-1">
                {["all","active","scheduled","landed","cancelled"].map(s => (
                  <button key={s} onClick={() => setStatusFilter(s)}
                    className={`px-2.5 py-1 text-xs rounded-sm capitalize transition-all border ${statusFilter === s ? "bg-primary/15 border-primary text-primary font-bold" : "bg-transparent border-transparent text-muted-foreground hover:text-foreground"}`}
                    data-testid={`filter-${s}`}
                  >{s}</button>
                ))}
              </div>
              <div className="ml-auto text-xs text-muted-foreground">{filtered.length} flights</div>
              {simLoading && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader size={10} className="animate-spin" /> Simulating crew...</div>}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card border-b border-border z-10">
                  <tr>
                    {["Flight", "Status", "Route", "Dep Time", "Crew (Simulated)", "FDP", "Aircraft"].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs text-muted-foreground font-bold uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground text-xs">No flights match your filter</td></tr>
                  ) : filtered.map(f => (
                    <FlightRow key={f.flight_iata} flight={f} assignment={assignments[f.flight_iata]} onClick={() => setSelected(f)} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Flight detail modal */}
      {selected && !triggerFlight && (
        <FlightDetailModal
          flight={selected}
          assignment={assignments[selected.flight_iata]}
          onClose={() => setSelected(null)}
          onTrigger={() => setTriggerFlight(selected)}
        />
      )}

      {/* Trigger disruption modal */}
      {triggerFlight && (
        <TriggerDisruptionModal
          flight={triggerFlight}
          assignment={assignments[triggerFlight.flight_iata]}
          onClose={() => setTriggerFlight(null)}
          onSuccess={handleDisruptionSuccess}
        />
      )}
    </div>
  );
}
